<template>
  <div class="grid grid-rows-[auto_1fr] items-center h-full text-my-gray">
    <ThreadsNav class="w-full" show-back>
      <template #center>
        <div class="text-xl font-semibold">
          {{ $t(`Payment.incomeRecord`) }}
        </div>
      </template>
    </ThreadsNav>
    <ThreadList
      ref="promptListRef"
      :on-pull-load="() => fetchList(true)"
      :on-scroll-load="fetchList"
      class="px-2 w-full"
    >
      <div v-for="item in list" :key="item.id" class="mb-4">
        <div
          class="grid grid-cols-[auto_1fr_auto] grid-rows-[auto_auto] items-center"
        >
          <div class="w-8 row-span-2">
            <BaseImg :url="item.mate.mediaUrl" :width="80" />
          </div>
          <h2 class="ml-2 text-white">{{ item.mate.name }}</h2>

          <div class="text-primary text-base text-end">
            +${{
              (
                (item.transactable.totalPrice ||
                  item.transactable.amount ||
                  item.amount) - (item.transactable.platformFeeAmount || 0)
              ).toFixed(2)
            }}
            <span
              v-if="item.transactable.platformFeeAmount"
              class="block text-my-gray text-xs w-[150px]"
              v-html="
                $t(`Payment.fee`, {
                  platformFeeAmount: item.transactable.platformFeeAmount,
                })
              "
            />
          </div>
          <div class="flex items-center pl-0.5">
            <GenderBadge :gender="item.mate.gender" class="w-5" padding />
            <span>
              {{ $t(`Home.type.${item.mate.type}`) }}
            </span>
          </div>
          <div class="text-end">
            {{ moment(item.dtCreate).format(`YYYY-MM-DD HH:mm:ss`) }}
          </div>
        </div>
        <div class="bg-my-dark p-2.5 mt-1.5">
          <div class="flex items-center gap-2">
            <b
              class="text-white"
              @click="
                router.push({
                  name: 'mateHome',
                  params: {
                    uuid: getGiver(item.transactable)?.uuid,
                  },
                })
              "
            >
              {{ getGiver(item.transactable)?.name }}
            </b>
            <div class="flex items-center">
              <span>{{ $t(`Payment.type.tip`) }}</span>

              <!-- <template v-if="item.transactable.crystalQuantity || item.transactable.totalCrystalPrice">
                <CrystalIcon />
                <b class="text-primary mr-1">
                  {{ item.transactable.crystalQuantity || item.transactable.totalCrystalPrice }}
                </b>
                (
                <b :class="[
                  'text-primary', 
                  !item.transactable.crystalQuantity || item.transactable.totalCrystalPrice && 'ml-1'
                ]">
                  ${{ item.transactable.amount || item.transactable.totalPrice }}
                </b>
                )
              </template> -->

              <b :class="['text-primary ml-1']"
                >${{
                  item.transactable?.totalPrice ||
                  item.transactable?.amount ||
                  item.amount
                }}</b
              >
            </div>
            <span
              v-if="item.transactableType === 'tip' && item.transactable.text"
            >
              {{ $t(`Payment.leaveMessage`) }} :
            </span>
          </div>
          <p v-if="item.transactableType === 'tip' && item.transactable.text">
            {{ item.transactable.text }}
          </p>
          <template v-if="item.transactable.tippableType !== 'mate'">
            <div class="bg-[#3a3a3a] mt-1 py-1 px-2.5">
              <div class="flex items-center">
                <p class="flex-1 line-clamp-1 mr-1 h-5">
                  {{ getTypoInfo(item) }}
                </p>

                <p
                  v-if="item.transactableType === 'tip'"
                  class="bg-[#303030] py-1 px-2.5 rounded cursor-pointer"
                  @click="clickOrigin(item.transactable)"
                >
                  {{ $t(`Payment.originContent`) }}
                </p>
              </div>

              <ul
                v-if="item.transactable.itemzz"
                class="grid grid-cols-3 gap-1 mt-1"
              >
                <li v-for="paid in item.transactable.itemzz" :key="paid.id">
                  <img
                    v-if="isImage(paid.media.url)"
                    :src="paid.media.url"
                    alt=""
                    class="rounded-lg overflow-hidden"
                  />
                  <video
                    v-else
                    :poster="paid.media.videoCoverUrl"
                    :src="paid.media.url"
                  />
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </ThreadList>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { ApiGetTransactionPage } from "@/apis/payment";
import moment from "moment-timezone";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { isImage } from "@/modules/common";

const promptListRef = ref<null | ThreadListInstance>(null);
const list = ref<TransactionView[]>([]);
const PER_PAGE = 12;
const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const fetchList = async (isReload = false) => {
  // ApiList
  if (isReload) {
    list.value = [];
    promptListRef.value?.resetScrollLoadingState();
    return true;
  }
  const page = Math.floor(list.value.length / PER_PAGE) + 1;
  const {
    page: { data },
  } = await ApiGetTransactionPage({
    pagination: {
      page,
      perPage: PER_PAGE,
    },
    filter: {
      type: 1,
      mateId: route.query.mateId ? Number(route.query.mateId) : undefined,
    },
    sort: {
      field: "dtCreate",
      order: "desc",
    },
  });
  const validData = data.filter((item) => item.transactable);
  list.value = [...list.value, ...validData];
  return data.length === PER_PAGE;
};

const getTypoInfo = (tx: TransactionView) => {
  if (tx.transactableType === "tip") {
    const { tippableType, tippable } = tx.transactable;
    let text = t(`Community.${tippableType}`) + ": ";
    if (tippableType === "galGame") text += tippable.name;
    else if (tippableType === "story") text += tippable.title;
    else text += tippable.text;
    return text;
  } else if (tx.transactableType === "admireSubscriptionFee") {
    return t(`purchase.item.sub`);
  } else if (tx.transactableType === "mediaOrder") {
    return t(`purchase.item.pay`);
  } else if (tx.transactableType === `App\\Models\\ChatMessage`) {
    return t(`purchase.item.chat`);
  }
};

const getGiver = (p: Payment) => {
  if (!p) return null;
  return (
    p.subscription?.admirer || p.purchaser || p.giver || p.mateSession?.myMate
  );
};

const clickOrigin = (p: Payment) => {
  const { tippableType, tippable } = p;
  if (tippableType === "galGame") {
    router.push({
      name: "galGameDetail",
      params: { uuid: tippable.uuid },
    });
  } else if (tippableType === "story") {
    router.push({
      name: "readStory",
      params: { uuid: tippable.uuid },
    });
  } else {
    router.push({
      name: "threadDetail",
      params: { uuid: tippable.uuid },
    });
  }
};

onMounted(async () => {});
</script>
